import React from 'react';
import PropTypes from 'prop-types';

const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  theme = 'coral',
  disabled = false,
  onClick,
  className = '',
  type = /** @type {'button' | 'submit' | 'reset'} */ ('button'),
  ...props
}) => {
  // Base (non-interactive) classes
  const baseRoot = [
    'inline-flex items-center justify-center gap-2.5 rounded-full',
    'font-hg-sans font-bold select-none',
    'relative overflow-hidden',
    'focus:outline-none',
  ];

  // Interactive vs disabled classes
  const interactive = disabled
    ? [
        'cursor-not-allowed opacity-60',
        'transition-none', // kills animations
        'shadow-none translate-y-0', // no lift/move
        'focus:ring-0', // no ring
      ]
    : [
        'cursor-pointer',
        'transition-all duration-300 ease-out',
        'transform translate-y-0 shadow-md',
        'hover:-translate-y-0.5 hover:shadow-lg',
        'active:translate-y-0 active:shadow-md active:transition-all active:duration-100',
        'focus:ring-2 focus:ring-opacity-30',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white',
      ];

  // Variant classes (strip hover/active when disabled)
  const getVariantClasses = (variant, theme, disabled) => {
    if (disabled) {
      return ['bg-gray-20 text-gray-70 border border-gray-20'];
    }

    // Theme-specific color configurations
    const themeColors = {
      coral: {
        primary: [
          'bg-coral-90 text-white',
          'hover:bg-red-600',
          'active:bg-red-700',
          'focus:ring-coral-90',
        ],
        secondary: [
          'bg-white text-coral-90 border-2 border-transparent',
          'hover:bg-coral-90 hover:text-white hover:border-coral-90',
          'active:bg-red-600 active:border-red-600',
          'focus:ring-coral-90',
        ],
        outline: [
          'bg-transparent text-coral-90 border-2 border-coral-90',
          'hover:bg-coral-90 hover:text-white',
          'active:bg-red-600 active:border-red-600',
          'focus:ring-coral-90',
        ],
        ghost: [
          'bg-transparent text-coral-90 border-none shadow-none',
          'hover:bg-coral-90 hover:bg-opacity-10 hover:text-red-600',
          'active:bg-coral-90 active:bg-opacity-20 active:text-red-700',
          'focus:ring-coral-90',
        ],
      },
      'deep-blue': {
        primary: [
          'bg-deep-blue-30 text-white border-1 border-deep-blue-30',
          'hover:bg-deep-blue-50',
          'active:bg-deep-blue-10',
          'focus:ring-deep-blue-50',
        ],
        secondary: [
          'bg-white text-deep-blue-70 border-1 border-transparent',
          'hover:bg-deep-blue-70 hover:text-white hover:border-deep-blue-70',
          'active:bg-deep-blue-50 active:border-deep-blue-50 active:text-white',
          'focus:ring-deep-blue-50',
        ],
        outline: [
          'bg-transparent text-deep-blue-70 border-1 border-deep-blue-70',
          'hover:bg-deep-blue-70 hover:text-white',
          'active:bg-deep-blue-50 active:border-deep-blue-50 active:text-white',
          'focus:ring-deep-blue-50',
        ],
        ghost: [
          'bg-transparent text-deep-blue-70 border-none shadow-none',
          'hover:bg-deep-blue-70 hover:bg-opacity-10 hover:text-deep-blue-50',
          'active:bg-deep-blue-70 active:bg-opacity-20 active:text-deep-blue-30',
          'focus:ring-deep-blue-50',
        ],
      },
    };

    const selectedTheme = themeColors[theme] || themeColors.coral;
    return selectedTheme[variant] || selectedTheme.primary;
  };

  // Sizes
  const getSizeClasses = (size) => {
    const sizes = {
      small: 'px-4 py-2 text-xs leading-4 min-h-8',
      medium: 'px-6 py-3 text-sm leading-5 min-h-11',
      large: 'px-8 py-4 text-base leading-6 min-h-14',
    };
    return sizes[size] || sizes.medium;
  };

  const buttonClasses = [
    ...baseRoot,
    ...interactive,
    ...getVariantClasses(variant, theme, disabled),
    getSizeClasses(size),
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'ghost']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  theme: PropTypes.oneOf(['coral', 'deep-blue']),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
};

export default Button;
